import { Button, Input, Label, Modal } from '@/components/basic';
import { LoadingSpinner } from '@/components/util/LoadingSpinner';
import OfficeFilterDropdown from '@/components/util/OfficeFilterDropdown';
import { api } from '@/lib/api/api';
import { classNames } from '@/lib/classNames';
import { ResGetStation } from '@gettactic/api';
import { useAuthenticated } from '@gettactic/hooks';
import { Switch } from '@headlessui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

type Props = {
  setEditStation: React.Dispatch<React.SetStateAction<ResGetStation | null>>;
  station: ResGetStation;
};

type FormUpdate = {
  name: string;
  office_id: string;
  area_id: string;
  type: string;
  status: string;
  is_enabled: boolean;
  settings: ResGetStation['settings'];
};

function SwitchComponent({
  value,
  onChange,
  label
}: {
  label: string;
  value: boolean;
  onChange: (x: boolean) => void;
}) {
  return (
    <Switch.Group>
      <div className="flex items-center">
        <Switch.Label className="mr-4 min-w-[60px]">{label}</Switch.Label>
        <Switch
          checked={!!value}
          onChange={onChange}
          className={`${
            value ? 'bg-primary' : 'bg-gray-200'
          } relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
        >
          <span
            className={classNames(
              value ? 'translate-x-5' : 'translate-x-0',
              'pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
            )}
          >
            <span
              className={classNames(
                value
                  ? 'opacity-0 duration-100 ease-out'
                  : 'opacity-100 duration-200 ease-in',
                'absolute inset-0 flex h-full w-full items-center justify-center transition-opacity'
              )}
              aria-hidden="true"
            >
              <svg
                className="h-3 w-3 text-gray-400"
                fill="none"
                viewBox="0 0 12 12"
              >
                <path
                  d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span
              className={classNames(
                value
                  ? 'opacity-100 duration-200 ease-in'
                  : 'opacity-0 duration-100 ease-out',
                'absolute inset-0 flex h-full w-full items-center justify-center transition-opacity'
              )}
              aria-hidden="true"
            >
              <svg
                className="h-3 w-3 text-orange-600"
                fill="currentColor"
                viewBox="0 0 12 12"
              >
                <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
              </svg>
            </span>
          </span>
        </Switch>
      </div>
    </Switch.Group>
  );
}

export function EditStationModal({
  setEditStation,
  station
}: Props): JSX.Element {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const offices = authenticatedUser.offices?.offices ?? [];
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const defaultValues: FormUpdate = {
    name: station.name,
    office_id: station.office_id,
    area_id: station.area_id,
    type: station.type,
    status: station.status,
    is_enabled: station.is_enabled,
    settings: station.settings
  };
  const { register, handleSubmit, control } = useForm<FormUpdate>({
    defaultValues
  });

  const submitMutation = useMutation(
    async (request: FormUpdate) => {
      const res = await api.client.stations.update(station.id, request);
      return { res, changedOffice: station.office_id !== request.office_id };
    },
    {
      onSuccess: ({ changedOffice }) => {
        setEditStation(null);
        if (changedOffice) {
          toast.success('The station has been moved to a new office.');
        } else {
          toast.success('Changes saved');
        }
      },
      onError: (err) => {
        console.error('Error saving station', err);
        toast.error('Failed to save changes');
      },
      onSettled: () => {
        setLoading(false);
        queryClient.invalidateQueries(['stations']);
      }
    }
  );

  return (
    <Modal
      title={`Edit Station`}
      opened={true}
      onClose={() => setEditStation(null)}
      size={'lg'}
    >
      <form
        onSubmit={handleSubmit((data) => {
          setLoading(true);
          submitMutation.mutate(data);
        })}
      >
        <div>
          <div className="mt-4 grid grid-cols-1 items-center sm:grid-cols-6">
            <div className="sm:col-span-4">
              <Label htmlFor="name">
                <span>Name</span>
                <Input
                  className="max-w-sm"
                  type="text"
                  {...register('name', { required: true })}
                />
              </Label>
            </div>
            <div className="mt-4 inline-flex justify-self-end p-4 sm:col-span-2">
              <Controller
                control={control}
                name="is_enabled"
                render={({ field: { onChange, value } }) => (
                  <SwitchComponent
                    label="Enabled"
                    value={!!value}
                    onChange={onChange}
                  />
                )}
              />
            </div>
          </div>

          <div className="mt-2  items-center justify-between  rounded-md">
            <div className="">
              <Label htmlFor="name">
                <span>Office</span>
              </Label>
            </div>
            <Controller
              control={control}
              name="office_id"
              render={({ field: { onChange, value } }) => (
                <OfficeFilterDropdown
                  className="max-w-sm"
                  setOfficeId={(id) => {
                    onChange(id);
                  }}
                  officeId={value}
                  offices={offices}
                />
              )}
            />
          </div>
          <h4 className="my-4 text-xl font-medium text-secondary [.tactic-v2_&]:text-secondary-foreground">
            Station Features
          </h4>
          <div className="flex flex-col space-y-5">
            <div className="">
              <Controller
                control={control}
                name="settings.badges_enabled"
                render={({ field: { onChange, value } }) => (
                  <SwitchComponent
                    label="Badges"
                    value={!!value}
                    onChange={onChange}
                  />
                )}
              />
            </div>
            <div className="">
              <Controller
                control={control}
                name="settings.phone_number_enabled"
                render={({ field: { onChange, value } }) => (
                  <SwitchComponent
                    label="Phone"
                    value={!!value}
                    onChange={onChange}
                  />
                )}
              />
            </div>
          </div>
        </div>
        <Modal.Footer>
          <Button
            className="w-full sm:w-auto"
            layout="squared"
            variant="white"
            font="boldGray"
            onClick={() => setEditStation(null)}
          >
            Cancel
          </Button>
          <Button
            className="ml-3 w-full sm:w-auto"
            layout="squared"
            variant="primary"
            type="submit"
            disabled={loading}
          >
            {loading ? (
              <LoadingSpinner color="text-white" />
            ) : (
              <span>Save Changes</span>
            )}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
